import _toConsumableArray from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/createClass";

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Iterable } from '../../../common/iterator.js';
import { IndexTreeModel } from './indexTreeModel.js';
import { TreeError } from './tree.js';
import { mergeSort } from '../../../common/arrays.js';
export var ObjectTreeModel = /*#__PURE__*/function () {
  function ObjectTreeModel(user, list) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    _classCallCheck(this, ObjectTreeModel);

    this.user = user;
    this.rootRef = null;
    this.nodes = new Map();
    this.nodesByIdentity = new Map();
    this.model = new IndexTreeModel(user, list, null, options);
    this.onDidSplice = this.model.onDidSplice;
    this.onDidChangeCollapseState = this.model.onDidChangeCollapseState;
    this.onDidChangeRenderNodeCount = this.model.onDidChangeRenderNodeCount;

    if (options.sorter) {
      this.sorter = {
        compare: function compare(a, b) {
          return options.sorter.compare(a.element, b.element);
        }
      };
    }

    this.identityProvider = options.identityProvider;
  }

  _createClass(ObjectTreeModel, [{
    key: "setChildren",
    value: function setChildren(element) {
      var children = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Iterable.empty();
      var onDidCreateNode = arguments.length > 2 ? arguments[2] : undefined;
      var onDidDeleteNode = arguments.length > 3 ? arguments[3] : undefined;
      var location = this.getElementLocation(element);

      this._setChildren(location, this.preserveCollapseState(children), onDidCreateNode, onDidDeleteNode);
    }
  }, {
    key: "_setChildren",
    value: function _setChildren(location) {
      var _this = this;

      var children = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Iterable.empty();
      var onDidCreateNode = arguments.length > 2 ? arguments[2] : undefined;
      var onDidDeleteNode = arguments.length > 3 ? arguments[3] : undefined;
      var insertedElements = new Set();
      var insertedElementIds = new Set();

      var _onDidCreateNode = function _onDidCreateNode(node) {
        if (node.element === null) {
          return;
        }

        var tnode = node;
        insertedElements.add(tnode.element);

        _this.nodes.set(tnode.element, tnode);

        if (_this.identityProvider) {
          var id = _this.identityProvider.getId(tnode.element).toString();

          insertedElementIds.add(id);

          _this.nodesByIdentity.set(id, tnode);
        }

        if (onDidCreateNode) {
          onDidCreateNode(tnode);
        }
      };

      var _onDidDeleteNode = function _onDidDeleteNode(node) {
        if (node.element === null) {
          return;
        }

        var tnode = node;

        if (!insertedElements.has(tnode.element)) {
          _this.nodes.delete(tnode.element);
        }

        if (_this.identityProvider) {
          var id = _this.identityProvider.getId(tnode.element).toString();

          if (!insertedElementIds.has(id)) {
            _this.nodesByIdentity.delete(id);
          }
        }

        if (onDidDeleteNode) {
          onDidDeleteNode(tnode);
        }
      };

      this.model.splice([].concat(_toConsumableArray(location), [0]), Number.MAX_VALUE, children, _onDidCreateNode, _onDidDeleteNode);
    }
  }, {
    key: "preserveCollapseState",
    value: function preserveCollapseState() {
      var _this2 = this;

      var elements = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Iterable.empty();

      if (this.sorter) {
        elements = mergeSort(_toConsumableArray(elements), this.sorter.compare.bind(this.sorter));
      }

      return Iterable.map(elements, function (treeElement) {
        var node = _this2.nodes.get(treeElement.element);

        if (!node && _this2.identityProvider) {
          var id = _this2.identityProvider.getId(treeElement.element).toString();

          node = _this2.nodesByIdentity.get(id);
        }

        if (!node) {
          return Object.assign(Object.assign({}, treeElement), {
            children: _this2.preserveCollapseState(treeElement.children)
          });
        }

        var collapsible = typeof treeElement.collapsible === 'boolean' ? treeElement.collapsible : node.collapsible;
        var collapsed = typeof treeElement.collapsed !== 'undefined' ? treeElement.collapsed : node.collapsed;
        return Object.assign(Object.assign({}, treeElement), {
          collapsible: collapsible,
          collapsed: collapsed,
          children: _this2.preserveCollapseState(treeElement.children)
        });
      });
    }
  }, {
    key: "rerender",
    value: function rerender(element) {
      var location = this.getElementLocation(element);
      this.model.rerender(location);
    }
  }, {
    key: "has",
    value: function has(element) {
      return this.nodes.has(element);
    }
  }, {
    key: "getListIndex",
    value: function getListIndex(element) {
      var location = this.getElementLocation(element);
      return this.model.getListIndex(location);
    }
  }, {
    key: "getListRenderCount",
    value: function getListRenderCount(element) {
      var location = this.getElementLocation(element);
      return this.model.getListRenderCount(location);
    }
  }, {
    key: "isCollapsible",
    value: function isCollapsible(element) {
      var location = this.getElementLocation(element);
      return this.model.isCollapsible(location);
    }
  }, {
    key: "setCollapsible",
    value: function setCollapsible(element, collapsible) {
      var location = this.getElementLocation(element);
      return this.model.setCollapsible(location, collapsible);
    }
  }, {
    key: "isCollapsed",
    value: function isCollapsed(element) {
      var location = this.getElementLocation(element);
      return this.model.isCollapsed(location);
    }
  }, {
    key: "setCollapsed",
    value: function setCollapsed(element, collapsed, recursive) {
      var location = this.getElementLocation(element);
      return this.model.setCollapsed(location, collapsed, recursive);
    }
  }, {
    key: "expandTo",
    value: function expandTo(element) {
      var location = this.getElementLocation(element);
      this.model.expandTo(location);
    }
  }, {
    key: "refilter",
    value: function refilter() {
      this.model.refilter();
    }
  }, {
    key: "getNode",
    value: function getNode() {
      var element = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

      if (element === null) {
        return this.model.getNode(this.model.rootRef);
      }

      var node = this.nodes.get(element);

      if (!node) {
        throw new TreeError(this.user, "Tree element not found: ".concat(element));
      }

      return node;
    }
  }, {
    key: "getNodeLocation",
    value: function getNodeLocation(node) {
      return node.element;
    }
  }, {
    key: "getParentNodeLocation",
    value: function getParentNodeLocation(element) {
      if (element === null) {
        throw new TreeError(this.user, "Invalid getParentNodeLocation call");
      }

      var node = this.nodes.get(element);

      if (!node) {
        throw new TreeError(this.user, "Tree element not found: ".concat(element));
      }

      var location = this.model.getNodeLocation(node);
      var parentLocation = this.model.getParentNodeLocation(location);
      var parent = this.model.getNode(parentLocation);
      return parent.element;
    }
  }, {
    key: "getElementLocation",
    value: function getElementLocation(element) {
      if (element === null) {
        return [];
      }

      var node = this.nodes.get(element);

      if (!node) {
        throw new TreeError(this.user, "Tree element not found: ".concat(element));
      }

      return this.model.getNodeLocation(node);
    }
  }]);

  return ObjectTreeModel;
}();